/**
 * The Google account card on the Settings page: the Cloud project phones are managed through.
 *
 *   * ITS OWN RECORD AND ITS OWN SAVE, like the mail and PayPal accounts: it carries a private key, and
 *     the tunables document is plaintext and broadcast to every agent.
 *   * THE KEY NEVER COMES BACK. An empty box keeps the stored key and clearing it is its own tick.
 *   * ONE REQUEST BUILDER FOR SAVE AND TEST, so the probe signs in with exactly what would be stored.
 *   * THE CHECKLIST IS ON THE CARD, because the steps happen in Google Cloud, where there is no way to
 *     ask this page what to do next.
 */
import * as mobile from '../../api/mobile.js';
import { describeError } from '../../api/http.js';
import type { AndroidAccountDto, UpdateAndroidAccountRequest } from '../../contracts.gen.js';
import { localDateTime } from '../../format.js';
import type { Card } from '../../ui/Card.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  field,
  fieldRow,
  GAP,
  h2,
  muted,
  relayout,
  stackOf,
  textBox,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import type { PageContext } from '../page.js';
import { TextArea } from 'webwin';

export interface AndroidAccountSection {
  card: Card;
  load(): Promise<void>;
}

const CHECKLIST = [
  '1. In Google Cloud (console.cloud.google.com) create a project, or pick one, and enable the Android Management API and the Cloud Pub/Sub API.',
  '2. Under IAM & Admin > Service Accounts, create a service account with the roles Android Management User and Pub/Sub Admin.',
  '3. On that account choose Keys > Add key > Create new key > JSON, and paste the whole file below.',
  '4. Save, then press Set up Pub/Sub - it creates the topic and subscription phones report through.',
  '5. Turn phone management on, then connect each customer to Google from their customer page.'
].join('\n');

export function androidAccountSection(ctx: PageContext, canEdit: boolean): AndroidAccountSection {
  const enabled = wrappingCheck('Manage Android phones');
  const key = new TextArea({ height: 110, hAlign: 'stretch', wrap: true, placeholder: '{ "type": "service_account", "project_id": ... }' });
  const keyState = muted('');
  const clearKey = wrappingCheck('Clear the stored key');
  const topic = textBox();
  const subscription = textBox();
  const ready = muted('');
  const sync = muted('');
  const updated = muted('');

  const status = new StatusLine();
  const saveButton = button('Save Google settings', () => void save(), true);
  const testButton = button('Test connection', () => void test());
  const setupButton = button('Set up Pub/Sub', () => void setup());
  const syncButton = button('Read phones now', () => void syncNow());

  for (const c of [enabled, key, clearKey, topic, subscription, saveButton, testButton, setupButton]) {
    c.enabled = canEdit;
  }

  const shell = card();
  shell.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2('Android phones (Google)'),
      muted(
        "Phones are managed through Google's Android Management API: Google's own app on the phone applies the policy this CRM publishes and reports back. Reports are read from a Pub/Sub subscription, so nothing on the internet has to reach this server. The key is stored encrypted and is never sent back to this page."
      ),
      muted(CHECKLIST),
      checkLine(enabled),
      field('Service account key (JSON)', key),
      keyState,
      checkLine(clearKey),
      fieldRow(field('Pub/Sub topic', topic), field('Pub/Sub subscription', subscription)),
      ready,
      sync,
      updated,
      stackOf(buttonRow(), saveButton, testButton, setupButton, syncButton),
      status.label
    )
  );

  function bind(a: AndroidAccountDto): void {
    enabled.checked = a.enabled;
    key.text = '';
    clearKey.checked = false;
    topic.text = a.topicName;
    subscription.text = a.subscriptionName;
    keyState.text = a.hasServiceAccountKey
      ? `A key is stored for ${a.serviceAccountEmail} in project ${a.projectId}. Leave the box empty to keep it.`
      : 'No key stored yet.';

    if (a.ready) {
      ready.text = 'Phone management is on. Connect a customer from their customer page to start enrolling phones.';
      ready.role = 'ok';
    } else {
      ready.text = a.enabled ? 'Not ready: a service-account key is needed.' : 'Phone management is off.';
      ready.role = a.enabled ? 'danger' : 'muted';
    }
    if (a.lastSyncUtc) {
      sync.text = a.lastSyncError
        ? `Last read from Google ${localDateTime(a.lastSyncUtc)} - it failed: ${a.lastSyncError}`
        : `Last read from Google ${localDateTime(a.lastSyncUtc)}.`;
      sync.role = a.lastSyncError ? 'danger' : 'muted';
    } else {
      sync.text = '';
    }
    sync.visible = sync.text.length > 0;
    syncButton.enabled = a.ready;
    updated.text = a.updatedUtc ? `Last changed ${localDateTime(a.updatedUtc)}${a.updatedBy ? ` by ${a.updatedBy}` : ''}.` : '';
    updated.visible = updated.text.length > 0;
    relayout(shell);
  }

  function buildRequest(): UpdateAndroidAccountRequest {
    const typed = key.text.trim();
    return {
      enabled: enabled.checked,
      serviceAccountKeyJson: clearKey.checked ? '' : typed.length > 0 ? typed : null,
      topicName: topic.text.trim() || null,
      subscriptionName: subscription.text.trim() || null
    };
  }

  function setBusy(busy: boolean): void {
    for (const b of [saveButton, testButton, setupButton]) b.enabled = canEdit && !busy;
  }

  async function save(): Promise<void> {
    setBusy(true);
    status.busy('Saving…');
    try {
      const saved = await mobile.updateAndroidAccount(buildRequest());
      if (ctx.cancelled()) return;
      bind(saved);
      status.ok('Google settings saved.');
    } catch (error) {
      if (!ctx.cancelled()) status.error('Could not save the Google settings: ' + describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function test(): Promise<void> {
    setBusy(true);
    status.busy('Signing in to Google…');
    const result = await mobile.testAndroidAccount({ settings: buildRequest() });
    setBusy(false);
    if (ctx.cancelled()) return;
    if (result.success) status.ok(result.message);
    else status.error(result.message);
  }

  async function setup(): Promise<void> {
    setBusy(true);
    status.busy('Setting up Pub/Sub…');
    const result = await mobile.setupAndroidPubSub();
    setBusy(false);
    if (ctx.cancelled()) return;
    if (result.success) status.ok(result.message);
    else status.error(result.message);
  }

  async function syncNow(): Promise<void> {
    syncButton.enabled = false;
    status.busy('Reading every phone from Google…');
    const result = await mobile.syncAndroid();
    if (ctx.cancelled()) return;
    if (result.success) status.ok(result.message);
    else status.error(result.message);
    try {
      bind(await mobile.getAndroidAccount(ctx.dispose.signal()));
    } catch {
      syncButton.enabled = true;
    }
  }

  return {
    card: shell,
    async load() {
      try {
        const a = await mobile.getAndroidAccount(ctx.dispose.signal());
        if (!ctx.cancelled()) bind(a);
      } catch (error) {
        if (!ctx.cancelled()) status.error('Could not load the Google settings: ' + describeError(error));
      }
    }
  };
}
